<template>
  <v-row class="align-center">
    <v-col cols="1" style="display: flex; align-items: center">
      <div class="d-flex flex-column mr-1">
        <v-icon size="small" @click="$emit('rowUp', rowNum)">mdi-menu-up</v-icon>
        <v-icon size="small" @click="$emit('rowDown', rowNum)">mdi-menu-down</v-icon>
      </div>
      <MegaCell v-model:data="data.name" :row="rowNum" :col="0" :dataType="'String'" placeholder="Name"></MegaCell>
    </v-col>
    <v-col cols="1">
      <MegaSelect
        :row="rowNum"
        :col="1"
        :options="spiceRackSizes"
        v-model:data="data.size"
        placeholder="Select size"
        @change="sizeChange"
      ></MegaSelect>
    </v-col>
    <v-col cols="1">
      <MegaSelect
        :row="rowNum"
        :col="2"
        :options="spiceRackDowelMaterials"
        v-model:data="data.dowel"
        placeholder="Select dowel"
      ></MegaSelect>
    </v-col>
    <v-col cols="2">
      <MegaSelect
        :row="rowNum"
        :col="3"
        :options="spiceRackMaterialTypes"
        v-model:data="data.materialType"
        placeholder="Select type"
        @change="materialTypeChange"
      ></MegaSelect>
    </v-col>
    <v-col cols="2" class="d-flex align-center">
      <!-- Frame for Laminate (diff) -->
      <template v-if="data.materialType === 'laminate-diff'">
        <div v-if="data.frame" class="colour mr-2" :style="{ 'background-color': findMaterialHex(data.frame) }"></div>
        <MegaSelect
          :row="rowNum"
          :col="4"
          :options="frameMaterialsDiff"
          v-model:data="data.frame"
          placeholder="Select frame"
          noOptions="No 18mm materials available"
        ></MegaSelect>
      </template>
      <!-- Frame for Laminate (main) -->
      <template v-else-if="data.materialType === 'laminate-main'">
        <div v-if="data.frame" class="colour mr-2" :style="{ 'background-color': findMaterialHex(data.frame) }"></div>
        <MegaSelect
          :row="rowNum"
          :col="4"
          :options="frameMaterialsMain"
          v-model:data="data.frame"
          placeholder="Select frame"
          noOptions="No 18mm fronts materials"
        ></MegaSelect>
      </template>
      <!-- Veneer material selection -->
      <template v-else-if="data.materialType === 'veneer'">
        <div
          v-if="data.materialType === 'veneer'"
          class="colour mr-2"
          :style="{ 'background-color': findMaterialHex(data.frame) }"
        ></div>
        <MegaSelect
          :row="rowNum"
          :col="4"
          :options="veneerMaterials"
          v-model:data="data.frame"
          placeholder="Select veneer"
          noOptions="No 18mm grain materials"
        ></MegaSelect>
      </template>
    </v-col>
    <v-col cols="1">
      <span class="text-caption">{{ data.w || '-' }}</span>
    </v-col>
    <v-col cols="1">
      <span class="text-caption">{{ data.h || '-' }}</span>
    </v-col>
    <v-col cols="1">
      <span class="text-caption">{{ data.d || '-' }}</span>
    </v-col>
    <v-col cols="1">
      <span class="text-caption">{{ data.shelves || '-' }}</span>
    </v-col>
    <v-col class="d-flex" :order="wonky ? 'first' : 'last'" :class="[{ 'justify-end': !wonky }]" cols="1">
      <v-tooltip location="top" :open-delay="500">
        <template v-slot:activator="{ props }">
          <v-icon size="small" class="mr-1" @click="$emit('copy', rowNum)" v-bind="props">mdi-content-copy</v-icon>
        </template>
        <span>Copy</span>
      </v-tooltip>
      <v-tooltip location="top" :open-delay="500">
        <template v-slot:activator="{ props }">
          <v-icon size="small" class="mr-1" @click="$emit('copyBottom', rowNum)" v-bind="props"
            >mdi-arrow-collapse-down</v-icon
          >
        </template>
        <span>Copy to bottom</span>
      </v-tooltip>
      <v-tooltip location="top" :open-delay="500">
        <template v-slot:activator="{ props }">
          <v-icon size="small" @click="$emit('delete', rowNum)" v-bind="props">mdi-delete</v-icon>
        </template>
        <span>Delete</span>
      </v-tooltip>
    </v-col>
  </v-row>
</template>

<script>
import productData from '@/js/productData'

import MegaSelect from '@/components/quoting/items/MegaSelect'
import MegaCell from '@/components/quoting/items/MegaCell'

export default {
  name: 'SpiceRack',
  props: ['rowNum', 'data', 'materials', 'pricing', 'wonky'],
  components: { MegaCell, MegaSelect },
  data() {
    return {
      productData: productData,
      spiceRackSizes: productData.spiceRackSizes,
      spiceRackDowelMaterials: productData.spiceRackDowelMaterials,
      spiceRackMaterialTypes: productData.spiceRackMaterialTypes
    }
  },
  computed: {
    // Laminate (diff): 18mm materials NOT used in any product type
    frameMaterialsDiff() {
      return this.materials.filter((material) => {
        if (material.thickness != 18 || material.hasGrain) return false
        // Check if material is NOT used in any product type (itemCount === 0 for all)
        const productTypes = ['fronts', 'coverPanels', 'urtils', 'pantries']
        return productTypes.every((type) => {
          const itemCount = this.pricing?.[type]?.[material.value]?.itemCount
          return !itemCount || itemCount === 0
        })
      })
    },
    // Laminate (main): 18mm materials that ARE used in at least one product type
    frameMaterialsMain() {
      return this.materials.filter((material) => {
        if (material.thickness != 18 || material.hasGrain) return false
        // Check if material IS used in at least one product type (itemCount > 0 for any)
        const productTypes = ['fronts', 'coverPanels', 'urtils', 'pantries']
        return productTypes.some((type) => {
          const itemCount = this.pricing?.[type]?.[material.value]?.itemCount
          return itemCount && itemCount > 0
        })
      })
    },
    // Veneer: 18mm materials with grain
    veneerMaterials() {
      return this.materials.filter((material) => {
        return material.thickness == 18 && material.hasGrain
      })
    }
  },
  methods: {
    sizeChange() {
      const preset = productData.getSpiceRackSize(this.data.size)
      if (preset) {
        this.data.w = preset.w
        this.data.h = preset.h
        this.data.d = preset.d
        this.data.shelves = preset.shelves
      }
    },
    materialTypeChange() {
      this.data.frame = null
    },
    findMaterialHex(materialCode) {
      if (!materialCode) return []
      const match = this.materials.find((material) => {
        return material.value === materialCode
      })
      return match?.hex
    }
  }
}
</script>

<style lang="scss" scoped>
.row {
  border-bottom: solid 1px #eee;
  .col {
    padding-top: 0px;
    padding-bottom: 0px;
  }
}
::v-deep .v-text-field__prefix {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
  margin-right: 10px;
}

.colour {
  min-width: 25px;
  width: 25px;
  height: 25px;
  border: solid 1px #999;
  border-radius: 3px;
  margin-right: 12px;
}
</style>
